import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LayoutDashboard, RefreshCw, Rocket } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { WaapEmptyCard } from './WaapEmptyCard';
import {
  useWaapDashboardCharts,
  useRefreshWaapDashboardCharts,
  WAAP_DASHBOARD_WINDOW_DAYS,
  type WaapChart,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import { WaapChartWidget } from './dashboard/WaapChartWidget';
import styles from './DashboardTab.module.css';

/**
 * WAAP Dashboard — account-level overview rendered as NATIVE charts (no Superset
 * iframe). The BE pre-normalizes the customer's BI overview into a list of typed
 * widgets (`useWaapDashboardCharts`); we lay them out by kind: a row of scalar
 * KPIs, then the pie/table breakdowns, then the time-series trends.
 *
 * This is the console root, so there's no website picker: the dashboard
 * aggregates the whole account. The BE resolves the Conversant customer from any
 * of the account's domains, so we pass the first/selected website's id only as
 * that resolver handle.
 */
export function DashboardTab() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { selected, isLoading: websitesLoading } = useWaapWebsite();
  const domainId = selected?.domainId;
  const { data, isLoading, isError, error } = useWaapDashboardCharts(domainId);
  const refresh = useRefreshWaapDashboardCharts(domainId);

  function handleRefresh() {
    refresh.mutate(undefined, {
      onSuccess: () => show({ tone: 'success', title: 'Dashboard refreshed' }),
      onError: () =>
        show({ tone: 'danger', title: 'Refresh failed', description: 'Could not fetch the latest data.' }),
    });
  }

  const groups = useMemo(() => {
    const charts = data?.charts ?? [];
    const numbers: WaapChart[] = [];
    const split: WaapChart[] = [];
    const wide: WaapChart[] = [];
    for (const c of charts) {
      if (c.kind === 'number') numbers.push(c);
      else if (c.kind === 'timeseries') wide.push(c);
      else split.push(c); // pie + table
    }
    return { numbers, split, wide };
  }, [data]);

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[]} />}
        title="Dashboard"
        actions={
          <Button
            variant="secondary"
            startIcon={<RefreshCw size={15} className={refresh.isPending ? styles.spin : undefined} />}
            onClick={handleRefresh}
            disabled={!domainId || isLoading || refresh.isPending}
          >
            {refresh.isPending ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      />

      {websitesLoading ? (
        <Skeleton height={520} />
      ) : !domainId ? (
        <WaapEmptyCard
          icon={<LayoutDashboard size={20} />}
          title="No websites yet"
          description="Onboard a website to start accelerating and protecting it with WAAP."
          actions={
            <Button
              startIcon={<Rocket size={16} />}
              onClick={() => navigate(`/services/waap/${serviceId}/onboarding`)}
            >
              Onboarding Domain Now
            </Button>
          }
        />
      ) : isLoading ? (
        <Skeleton height={520} />
      ) : data === null ? (
        <WaapEmptyCard
          icon={<LayoutDashboard size={20} />}
          title="Dashboard analytics not configured"
          description="The analytics board is not set up for this environment yet. Ask an administrator to enable it."
        />
      ) : isError ? (
        <Alert tone="danger" title="Couldn't load dashboard">
          {errMessage(error)}
        </Alert>
      ) : (
        <div className={styles.grids}>
          {/* The Dashboard board is a fixed 30-day account overview, server-cached
              up to 8h — tell the user the window + how to force-refresh. */}
          <Alert tone="info" title={`Showing the last ${WAAP_DASHBOARD_WINDOW_DAYS} days`}>
            This account overview covers the last {WAAP_DASHBOARD_WINDOW_DAYS} days and is cached for
            up to 8 hours. Use Refresh for the latest data.
          </Alert>
          {groups.numbers.length > 0 && (
            <div className={styles.numberGrid}>
              {groups.numbers.map((c) => (
                <WaapChartWidget key={c.key} chart={c} />
              ))}
            </div>
          )}
          {groups.split.length > 0 && (
            <div className={styles.splitGrid}>
              {groups.split.map((c) => (
                <WaapChartWidget key={c.key} chart={c} />
              ))}
            </div>
          )}
          {groups.wide.length > 0 && (
            <div className={styles.wideGrid}>
              {groups.wide.map((c) => (
                <WaapChartWidget key={c.key} chart={c} />
              ))}
            </div>
          )}
        </div>
      )}
    </Container>
  );
}
